import ModuleBase from '../shared/ModuleBase.js';

/**
 * LangSwitch — A11y-Dropdown für den Sprachumschalter (W3C APG Menu-Button-Muster).
 *
 * Trigger-Button (data-ref="toggle") klappt eine Sprachliste (data-ref="menu") auf.
 * Items = echte Links (data-ref="item[]"). Tastatur:
 *   - ArrowDown / Enter / Space am Trigger → öffnen + erstes Item fokussieren
 *   - ArrowUp am Trigger → öffnen + letztes Item fokussieren
 *   - ArrowUp/Down im Menü → navigieren (wrap-around), Home/End → Anfang/Ende
 *   - ESC → schließen + Focus zurück zum Trigger
 *   - Tab / Outside-Click → schließen
 * Das `hidden`-Attribut hält das geschlossene Menü sauber aus dem A11y-Baum.
 */
export default class LangSwitch extends ModuleBase {
    constructor(el) {
        super(el);
        this._toggle = this.$refs.toggle;
        this._menu = this.$refs.menu;
        this._items = this.$refs.item || [];
        this._chevron = this.$refs.chevron || null;
        if (!this._toggle || !this._menu) return;
        this._bind();
    }

    _bind() {
        this._toggle.addEventListener('click', (e) => {
            e.preventDefault();
            this._isOpen() ? this._close() : this._open();
        });

        this._toggle.addEventListener('keydown', (e) => {
            if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                this._open();
                this._focusItem(0);
            } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                this._open();
                this._focusItem(this._items.length - 1);
            }
        });

        this._menu.addEventListener('keydown', (e) => this._onMenuKey(e));

        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && this._isOpen()) this._close({ returnFocus: true });
        });

        document.addEventListener('click', (e) => {
            if (this._isOpen() && !this.$el.contains(e.target)) this._close();
        });
    }

    _onMenuKey(e) {
        const idx = this._items.indexOf(document.activeElement);
        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                this._focusItem(idx + 1);
                break;
            case 'ArrowUp':
                e.preventDefault();
                this._focusItem(idx - 1);
                break;
            case 'Home':
                e.preventDefault();
                this._focusItem(0);
                break;
            case 'End':
                e.preventDefault();
                this._focusItem(this._items.length - 1);
                break;
            case 'Escape':
                e.preventDefault();
                this._close({ returnFocus: true });
                break;
            case 'Tab':
                this._close();
                break;
            default:
                break;
        }
    }

    _focusItem(i) {
        if (!this._items.length) return;
        const item = this._items[(i + this._items.length) % this._items.length];
        if (item) item.focus();
    }

    _open() {
        this._menu.hidden = false;
        this._toggle.setAttribute('aria-expanded', 'true');
        if (this._chevron) this._chevron.classList.add('rotate-180');
    }

    _close({ returnFocus = false } = {}) {
        this._menu.hidden = true;
        this._toggle.setAttribute('aria-expanded', 'false');
        if (this._chevron) this._chevron.classList.remove('rotate-180');
        if (returnFocus) this._toggle.focus();
    }

    _isOpen() {
        return this._toggle.getAttribute('aria-expanded') === 'true';
    }
}
